import React, { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Send, Mic, MicOff, Plus, AlertTriangle } from 'lucide-react'
import { useChat } from '@/contexts/ChatContext'
import { Button } from './ui/Button'
import { clsx } from 'clsx'

// TypeScript declarations for Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList
  resultIndex: number
}

interface SpeechRecognitionResultList {
  length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
  isFinal: boolean
  length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionAlternative {
  transcript: string
  confidence: number
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  start(): void
  stop(): void
  abort(): void
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: ((event: Event & { error: string }) => void) | null
  onend: (() => void) | null
  onstart: (() => void) | null
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition
    webkitSpeechRecognition: new () => SpeechRecognition
  }
}

export function MessageInput() {
  const { sendMessage, isConnected, requestHandoff } = useChat()
  const [message, setMessage] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [showActions, setShowActions] = useState(false)
  const [interimTranscript, setInterimTranscript] = useState('')
  const [speechSupported, setSpeechSupported] = useState(false)
  const [speechError, setSpeechError] = useState<string | null>(null)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const recognitionRef = useRef<SpeechRecognition | null>(null)

  // Check for speech recognition support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (SpeechRecognition) {
      setSpeechSupported(true)

      const recognition = new SpeechRecognition()
      recognition.continuous = true
      recognition.interimResults = true
      recognition.lang = 'en-US'

      recognition.onstart = () => {
        setIsRecording(true)
        setSpeechError(null)
      }

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let interim = ''
        let final = ''

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript
          if (event.results[i].isFinal) {
            final += transcript
          } else {
            interim += transcript
          }
        }

        if (final) {
          setMessage(prev => prev + final)
          setInterimTranscript('')
        } else {
          setInterimTranscript(interim)
        }
      }

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error)
        setIsRecording(false)
        setInterimTranscript('')

        switch (event.error) {
          case 'no-speech':
            setSpeechError('No speech detected. Try again.')
            break
          case 'audio-capture':
            setSpeechError('No microphone found. Check your device.')
            break
          case 'not-allowed':
            setSpeechError('Microphone access denied. Enable in settings.')
            break
          default:
            setSpeechError('Voice input error. Try typing instead.')
        }

        // Clear error after 3 seconds
        setTimeout(() => setSpeechError(null), 3000)
      }

      recognition.onend = () => {
        setIsRecording(false)
        setInterimTranscript('')
      }

      recognitionRef.current = recognition
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort()
      }
    }
  }, [])

  const toggleVoiceRecording = useCallback(() => {
    if (!recognitionRef.current) return

    if (isRecording) {
      recognitionRef.current.stop()
    } else {
      try {
        recognitionRef.current.start()
      } catch (error) {
        // Recognition might already be running
        console.error('Failed to start recognition:', error)
      }
    }
  }, [isRecording])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const finalMessage = (message + interimTranscript).trim()
    if (finalMessage && isConnected) {
      // Stop recording if active
      if (isRecording && recognitionRef.current) {
        recognitionRef.current.stop()
      }

      sendMessage(finalMessage)
      setMessage('')
      setInterimTranscript('')

      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value)

    const textarea = e.target
    textarea.style.height = 'auto'
    const newHeight = Math.min(textarea.scrollHeight, 120)
    textarea.style.height = `${newHeight}px`
  }

  const actionButtons = [
    {
      icon: AlertTriangle,
      label: 'Request Human Help',
      action: () => requestHandoff('User requested human assistance'),
      className: 'text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20'
    }
  ]

  const displayText = message + (interimTranscript ? ` ${interimTranscript}` : '')

  return (
    <div className="p-4">
      {/* Quick Actions */}
      <AnimatePresence>
        {showActions && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="mb-3 flex flex-wrap gap-2"
          >
            {actionButtons.map((action, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                onClick={() => {
                  action.action()
                  setShowActions(false)
                }}
                className={clsx('text-xs', action.className)}
              >
                <action.icon className="w-3 h-3 mr-1" />
                {action.label}
              </Button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handleSubmit} className="flex items-end space-x-3">
        {/* Additional Actions Button */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setShowActions(!showActions)}
          className="flex-shrink-0 focus-visible"
          disabled={!isConnected}
        >
          <motion.div
            animate={{ rotate: showActions ? 45 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <Plus className="w-5 h-5" />
          </motion.div>
        </Button>

        {/* Message Input */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={displayText}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder={
              !isConnected
                ? "Reconnecting..."
                : isRecording
                ? "Listening... speak now"
                : "Type or tap mic to speak..."
            }
            disabled={!isConnected}
            rows={1}
            className={clsx(
              'w-full px-4 py-3 pr-12 rounded-2xl border resize-none',
              'bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm',
              'border-slate-300 dark:border-slate-600',
              'text-slate-900 dark:text-slate-100',
              'placeholder-slate-500 dark:placeholder-slate-400',
              'focus:outline-none focus:ring-2 focus:ring-carnival-400 focus:border-transparent',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'text-sm leading-relaxed',
              'shadow-outdoor-sm',
              isRecording && 'ring-2 ring-red-400 border-red-400'
            )}
            style={{ minHeight: '48px' }}
          />

          {/* Voice Recording Button */}
          {speechSupported && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={toggleVoiceRecording}
              className={clsx(
                'absolute right-2 top-1/2 transform -translate-y-1/2',
                'focus-visible transition-colors',
                isRecording
                  ? 'text-red-500 bg-red-100 dark:bg-red-900/30 animate-pulse'
                  : 'text-slate-500 hover:text-carnival-500'
              )}
              disabled={!isConnected}
              title={isRecording ? 'Stop recording' : 'Start voice input'}
            >
              {isRecording ? (
                <MicOff className="w-4 h-4" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
            </Button>
          )}
        </div>

        {/* Send Button */}
        <Button
          type="submit"
          variant="default"
          size="icon"
          disabled={!displayText.trim() || !isConnected}
          className={clsx(
            'flex-shrink-0 bg-carnival-400 hover:bg-carnival-500',
            'text-carnival-900 shadow-outdoor-md',
            'focus-visible disabled:opacity-50',
            'transition-all duration-200'
          )}
        >
          <motion.div
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Send className="w-4 h-4" />
          </motion.div>
        </Button>
      </form>

      {/* Connection Status */}
      {!isConnected && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-2 text-center"
        >
          <span className="text-xs text-red-600 dark:text-red-400">
            Connection lost. Attempting to reconnect...
          </span>
        </motion.div>
      )}

      {/* Voice Recording Indicator */}
      <AnimatePresence>
        {isRecording && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mt-3 flex items-center justify-center space-x-3 py-2 px-4 bg-red-50 dark:bg-red-900/20 rounded-xl"
          >
            <motion.div
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
              className="w-3 h-3 bg-red-500 rounded-full"
            />
            <span className="text-sm font-medium text-red-600 dark:text-red-400">
              Listening... tap mic to stop
            </span>
            <div className="flex space-x-1">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={{ height: ['8px', '16px', '8px'] }}
                  transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.15 }}
                  className="w-1 bg-red-400 rounded-full"
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Interim Transcript Preview */}
      <AnimatePresence>
        {interimTranscript && !isRecording && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mt-2 text-center"
          >
            <span className="text-xs text-slate-500 italic">
              "{interimTranscript}"
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Speech Error */}
      <AnimatePresence>
        {speechError && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mt-2 text-center"
          >
            <span className="text-xs text-orange-600 dark:text-orange-400">
              {speechError}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Voice Not Supported Warning */}
      {!speechSupported && (
        <div className="mt-2 text-center">
          <span className="text-xs text-slate-500">
            Voice input not supported in this browser
          </span>
        </div>
      )}
    </div>
  )
}
